<script>
export default {
  name: "DidoExplore",
};
</script>

<script setup>
import { computed, ref, watch, inject, getCurrentInstance } from "vue";
import { createCatalogFilter } from "../utils";
import DidoExploreTextFilter from "./explore/TextFilter.vue";
import DidoExploreTypeFilter from "./explore/TypeFilter.vue";
import DidoExploreSortFilter from "./explore/SortFilter.vue";
import DidoExploreFacetFieldFilter from "./explore/FacetFieldFilter.vue";
import DidoPagination from "./Pagination.vue";
import api from "../api";

// get quasar Screen utils
const Screen = getCurrentInstance()?.appContext.provides._q_.screen;

// define Props
const props = defineProps({
  apiUrl: { type: String, required: true },
  exploreFilter: { type: [Object, String], required: false, default: () => {} },
});

// define events emitted
const emit = defineEmits([
  "click-apidoc",
  "click-card",
  "label-selection-on-card",
  "new-filter",
  "bad-configuration",
]);

// define reactive data for the object filter
const filter = ref(null);

// define reactive data for facets results
const facets = ref(null);

// define ractive data four counting items
const count = ref(-1);

// define reactive data for management of api call of facets
const apiCallDelayed = ref(null);

// define reactive data for showing facets in mini mode
const showFacetsInMiniMode = ref(false);

// define computed data to know if facets are displayed
const showFacets = computed(() => Screen.gt.xs || showFacetsInMiniMode.value);

// define computed data to check if widget is configured
const configured = computed(() => {
  if (!props.apiUrl) return false;
  if (!props.exploreFilter) return false;
  return true;
});

// define computed data for counter Text
const counterText = computed(() => {
  if (count.value === -2) return "Erreur d'accès aux données";
  if (count.value === -1) return "En cours de chargement";
  if (count.value < 2)
    return filter.value.itemsType === "dataset"
      ? `${count.value} jeu de données`
      : `${count.value} fichier de données`;
  if (count.value > 1)
    return filter.value.itemsType === "dataset"
      ? `${count.value} jeux de données`
      : `${count.value} fichiers de données`;
  return "";
});

// define computed data for sort clause as a string
const sortAsString = computed(() => {
  if (!filter.value.order) return "";
  if (filter.value.order.field === "title")
    return filter.value.order.sort === "asc" ? "title" : "-title";
  if (filter.value.order.field === "last_modified")
    return filter.value.order.sort === "asc"
      ? "last_modified"
      : "-last_modified";
  return "";
});

// define computed data for topics in facets
const topicsInFacets = computed(() =>
  facets.value ? facets.value.byTopics : [],
);
const topicsSelected = computed(() => filter.value.topics || []);

// define computed data for tags in facets
const tagsInFacets = computed(() => (facets.value ? facets.value.byTags : []));
const tagsSelected = computed(() => filter.value.tags || []);

// define computed data for zones in facets
const zonesInFacets = computed(() =>
  (facets.value ? facets.value.bySpatialZones : []).map((zone) => {
    let display = zone.display;
    if (zone.display === "France") display = "France entière";
    if (zone.display === "Metropolitan France")
      display = "France métropolitaine";
    return { value: zone.value, count: zone.count, display };
  }),
);
const zonesSelected = computed(() => filter.value.zones || []);

// define computed data for granumlarities in facets
const granularitiesInFacets = computed(() =>
  facets.value ? facets.value.bySpatialGranularity : [],
);
const granularitiesSelected = computed(() => filter.value.granularities || []);

// define computed data for frequencies in facets
const frequenciesInFacets = computed(() =>
  facets.value ? facets.value.byFrequency : [],
);
const frequenciesSelected = computed(() => filter.value.frequencies || []);

// define computed data for licenses in facets
const licensesInFacets = computed(() =>
  facets.value ? facets.value.byLicenses : [],
);
const licensesSelected = computed(() => filter.value.licenses || []);

// define computed data for params of facets
const facetsParams = computed(() => {
  let params = {};
  if (filter.value.text) params.text = filter.value.text;
  if (filter.value.topics.length) params.topics = filter.value.topics.join(",");
  if (filter.value.tags.length) params.tags = filter.value.tags.join(",");
  if (filter.value.licenses.length)
    params.licenses = filter.value.licenses.join(",");
  if (filter.value.zones.length) params.zones = filter.value.zones.join(",");
  if (filter.value.granularities.length)
    params.granularities = filter.value.granularities.join(",");
  if (filter.value.frequencies.length)
    params.frequencies = filter.value.frequencies.join(",");
  return params;
});

// inject variables for interaction with catalog
const catalogUrl = inject("catalogUrl", () => null);
const inCatalog = inject("inCatalog", null);

// define watchers
watch(
  () => [props.apiUrl, props.exploreFilter],
  () => init(),
  { deep: true },
);

// define methods
const init = () => {
  // if widget bad configured
  if (!configured.value) {
    filter.value = null;
    emit("bad-configuration");
    return;
  }
  // set filter from prop exploreFilter
  filter.value = createCatalogFilter(
    typeof props.exploreFilter === "string"
      ? JSON.parse(props.exploreFilter)
      : props.exploreFilter,
  );

  // loading facets
  loadFacets();
};
const changePagination = (newPage) => {
  filter.value.pagination = newPage;
  emit("new-filter", filter.value);
};
const changeTextFilter = (newText) => {
  filter.value.text = newText;
  filter.value.pagination = 1;
  loadFacets();
  emit("new-filter", filter.value);
};
const changeTypeFilter = (newType) => {
  filter.value.itemsType = newType;
  filter.value.pagination = 1;
  loadFacets();
  emit("new-filter", filter.value);
};
const changeSortClause = (newOrder) => {
  filter.value.order = newOrder;
  emit("new-filter", filter.value);
};
const changeFacet = ({ field, action, value }) => {
  if (action === "add") filter.value[field].push(value);
  if (action === "remove")
    filter.value[field] = filter.value[field].filter((t) => t !== value);
  filter.value.pagination = 1;
  loadFacets();
  emit("new-filter", filter.value);
};
const loadFacets = async () => {
  // delete apiCallDelayed
  clearTimeout(apiCallDelayed.value);
  apiCallDelayed.value = null;

  // set flag for loading
  count.value = -1;

  try {
    const apiResultFacets = await (filter.value.itemsType === "dataset"
      ? api.facetsDatasets(props.apiUrl, facetsParams.value)
      : api.facetsDatafiles(props.apiUrl, facetsParams.value));
    // if error with api results
    if (apiResultFacets.status !== "available") throw new Error();

    // set facets
    facets.value = apiResultFacets.facets;

    // set count
    count.value = facets.value.byTopics.reduce(
      (partial, topicFacet) => partial + topicFacet.count,
      0,
    );
  } catch {
    count.value = -2;
    if (!apiCallDelayed.value)
      apiCallDelayed.value = setTimeout(() => loadFacets, 10000);
  }
};
// init
init();
</script>

<template>
  <div class="dido-widget-explore">
    <!-- Bad configuration of widget -->
    <div v-if="!configured">
      <div>Impossible de charger le composant...</div>
    </div>
    <template v-else>
      <!-- Title and Text Search -->
      <div class="q-pa-lg dido-explore-header text-white">
        <div class="text-h3 text-bold text-center">Catalogue DiDo</div>
        <div class="q-mt-lg text-small text-center">
          L'application DiDo, ou Diffusion de données, met à disposition une
          série de jeux de données du SDES utilisant des formats ouverts et une
          <a
            v-if="inCatalog"
            class="text-info cursor-pointer"
            :href="catalogUrl({ type: 'apidoc' })"
            title="Voir la documentation de l'API DiDo"
            @click.prevent="emit('click-apidoc')"
            >API</a
          ><span v-else class="cursor-inherit">API</span> dédiée.
        </div>
        <div class="q-mt-lg row-all-center">
          <DidoExploreTextFilter
            class="col-xs-12 col-sm-9 col-md-6 col-lg-4 col-xl-3"
            :text="filter.text"
            @new-text="changeTextFilter"
          />
        </div>
      </div>
      <div
        class="q-mt-lg row justify-start items-start content-start border-box"
      >
        <div v-if="$q.screen.lt.sm" class="q-mb-sm">
          <QToggle
            v-model="showFacetsInMiniMode"
            :label="
              showFacetsInMiniMode
                ? 'Cacher les filtres :'
                : 'Afficher les filtres :'
            "
            left-label
            color="secondary"
            keep-color
            dense
            size="xs"
            class="text-small text-secondary"
          />
        </div>
        <div
          v-show="showFacets"
          class="col-md-3 col-sm-5 col-12 dido-explore-facets q-pa-md"
        >
          <!-- Topics Choice -->
          <DidoExploreFacetFieldFilter
            :items-in-facets="topicsInFacets"
            :items-selected="topicsSelected"
            facet-field="topics"
            facet-label="Thèmes"
            @update-facet="changeFacet"
          />
          <!-- Tags Choice -->
          <DidoExploreFacetFieldFilter
            :items-in-facets="tagsInFacets"
            :items-selected="tagsSelected"
            facet-field="tags"
            facet-label="Mots-clés"
            @update-facet="changeFacet"
          />
          <!-- Zones Choice -->
          <DidoExploreFacetFieldFilter
            :items-in-facets="zonesInFacets"
            :items-selected="zonesSelected"
            facet-field="zones"
            facet-label="Zones géographiques"
            @update-facet="changeFacet"
          />
          <!-- Granularities Choice -->
          <DidoExploreFacetFieldFilter
            :items-in-facets="granularitiesInFacets"
            :items-selected="granularitiesSelected"
            facet-field="granularities"
            facet-label="Granularités géographiques"
            :order="[
              'country',
              'fr:region',
              'fr:collectivite',
              'fr:departement',
              'fr:arrondissement',
              'fr:canton',
              'fr:epci',
              'fr:commune',
              'fr:iris',
              'poi',
              'other',
            ]"
            @update-facet="changeFacet"
          />
          <!-- Frequencies Choice -->
          <DidoExploreFacetFieldFilter
            :items-in-facets="frequenciesInFacets"
            :items-selected="frequenciesSelected"
            facet-field="frequencies"
            facet-label="Fréquences d'actualisation"
            :order="[
              'daily',
              'weekly',
              'monthly',
              'quarterly',
              'semiannual',
              'annual',
              'punctual',
              'irregular',
              'unknown',
            ]"
            @update-facet="changeFacet"
          />
          <!-- Licenses Choice -->
          <DidoExploreFacetFieldFilter
            :items-in-facets="licensesInFacets"
            :items-selected="licensesSelected"
            facet-field="licenses"
            facet-label="Licences"
            @update-facet="changeFacet"
          />
        </div>
        <div class="col-md-9 col-sm-7 col-12 q-pa-md">
          <DidoExploreTypeFilter
            :type="filter.itemsType"
            @new-type="changeTypeFilter"
          />
          <div
            class="row-between-center q-my-lg q-col-gutter-x-xl q-col-gutter-y-sm"
          >
            <!-- Counter -->
            <span class="text-h5 text-bold col-auto">
              {{ counterText }}
            </span>
            <!-- Order -->
            <span class="col-auto">
              <DidoExploreSortFilter
                :sort="sortAsString"
                @new-sort="changeSortClause"
              />
            </span>
          </div>
          <QSeparator spaced="xl" />
          <div class="border-box">
            <DidoPagination
              v-if="filter"
              :api-url="props.apiUrl"
              :pagination-filter="filter"
              @click-card="emit('click-card', $event)"
              @new-page="changePagination"
              @label-selection-on-card="emit('label-selection-on-card', $event)"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="sass" scoped>
.dido-explore-header
  background-color: #536779
  a
    text-decoration: none
  a:hover
    text-decoration: underline
.dido-explore-facets
  background-color: #f5f5fe
</style>
